/**
 * 바닥글
 * ---------------------------------------------------------------------------
 * 홍보 사이트(lumina)의 푸터와 같은 내용을 담는다. 같은 회사의 서비스인데
 * 사이트마다 사업자 정보가 다르게 보이면 신뢰를 잃는다.
 *
 * 왜 전자상거래 표시가 필요한가
 *   유료 구독을 받는 화면이 있는 서비스는 통신판매업자 표시 의무가 있다.
 *   상호·대표자·주소·전화·사업자등록번호·통신판매업 신고번호를 이용자가 쉽게
 *   볼 수 있는 곳에 둔다.
 *
 * 값은 코드에 박지 않는다
 *   회사 정보는 바뀐다. 주소가 바뀔 때마다 빌드를 새로 하는 대신 환경설정에서
 *   읽는다. 비어 있는 항목은 줄째로 빼므로, 값이 없다고 "주소: " 같은 빈 칸이
 *   남지 않는다.
 *
 *   다만 Vite 는 빌드 시점에 값을 번들에 박는다. 바꾸면 다시 빌드해야 한다.
 *   (브라우저에 내려가는 값이므로 비밀을 넣지 않는다 — 전부 공개 정보다)
 */

import { Layout, Space, Typography } from 'antd';

const { Text, Link } = Typography;

const env = import.meta.env;

/** 값이 비면 그 항목은 통째로 뺀다. */
function present(value: string | undefined): string | null {
  const v = (value ?? '').trim();
  return v === '' ? null : v;
}

/**
 * 약관·방침은 **작은 창**으로 띄운다.
 *
 * 새 탭으로 열면 읽고 나서 원래 탭을 다시 찾아야 한다. 모바일에서는 탭
 * 목록을 뒤져야 하고, 그 사이 보던 화면이 어디였는지 잊는다. 작은 창은
 * 닫는 순간 보던 화면으로 돌아온다. Hub·홍보 사이트도 같은 방식이다.
 *
 * 팝업이 막히면 새 탭으로라도 연다 — 약관을 못 보는 것보다 낫다.
 */
function openPolicyWindow(url: string, name: string) {
  const opened = window.open(url, name, 'width=860,height=900,scrollbars=yes,resizable=yes');
  if (!opened || opened.closed) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  opened.focus();
}

export function AppFooter() {
  const company = present(env.VITE_FOOTER_COMPANY);
  const address = present(env.VITE_FOOTER_ADDRESS);
  const copyright = present(env.VITE_FOOTER_COPYRIGHT);
  const privacyUrl = present(env.VITE_FOOTER_PRIVACY_URL);
  const termsUrl = present(env.VITE_FOOTER_TERMS_URL);

  const contacts = [
    { label: '대표이사', value: present(env.VITE_FOOTER_CEO) },
    { label: '대표전화', value: present(env.VITE_FOOTER_PHONE) },
    { label: '팩스', value: present(env.VITE_FOOTER_FAX) },
    { label: '이메일', value: present(env.VITE_FOOTER_EMAIL) },
  ].filter((x): x is { label: string; value: string } => x.value !== null);

  const business = [
    { label: '사업자등록번호', value: present(env.VITE_FOOTER_BIZ_REG_NO) },
    { label: '통신판매업 신고번호', value: present(env.VITE_FOOTER_MAIL_ORDER_NO) },
  ].filter((x): x is { label: string; value: string } => x.value !== null);

  // 넣을 것이 하나도 없으면 빈 띠만 남는다. 그럴 바엔 그리지 않는다.
  const empty =
    !company && !address && !copyright && contacts.length === 0 && business.length === 0;
  if (empty) return null;

  return (
    <Layout.Footer
      style={{
        background: '#0f172a',
        color: 'rgba(255,255,255,0.65)',
        fontSize: 12,
        lineHeight: 1.9,
        padding: '20px 24px',
      }}
    >
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        {(company || privacyUrl || termsUrl) && (
          <Space wrap size={12} style={{ justifyContent: 'space-between', width: '100%' }}>
            {company && (
              <Text strong style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>
                {company}
              </Text>
            )}
            <Space wrap size={12}>
              {/* href 는 남겨 둔다 — 주소 복사·새 탭 열기와 스크린 리더를 위해서다. */}
              {privacyUrl && (
                <Link
                  href={privacyUrl}
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openPolicyWindow(privacyUrl, 'worknhub-policy-privacy');
                  }}
                >
                  개인정보처리방침
                </Link>
              )}
              {termsUrl && (
                <Link
                  href={termsUrl}
                  style={{ color: 'rgba(255,255,255,0.75)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    openPolicyWindow(termsUrl, 'worknhub-policy-terms');
                  }}
                >
                  이용약관
                </Link>
              )}
            </Space>
          </Space>
        )}

        {address && <div>{address}</div>}

        {contacts.length > 0 && <div>{contacts.map(join).join('  |  ')}</div>}
        {business.length > 0 && <div>{business.map(join).join('  |  ')}</div>}

        {copyright && (
          <Text style={{ color: 'rgba(255,255,255,0.45)' }}>{copyright}</Text>
        )}
      </Space>
    </Layout.Footer>
  );
}

const join = (x: { label: string; value: string }) => `${x.label}: ${x.value}`;
